import { useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useDyteMeeting } from '@dytesdk/react-web-core';
import {
	DyteAudioVisualizer,
	DyteAvatar,
	DyteControlbarButton,
	DyteNameTag,
	DyteParticipantTile,
	DyteScreenshareView,
	defaultIconPack,
	DyteParticipantsAudio,
} from '@dytesdk/react-ui-kit';
import classNames from 'classnames';
import { DyteParticipant } from '@dytesdk/web-core';
import { exitFullSreen, requestFullScreen } from 'src/utils/browser';
import MeetingScreenWrapper from './MeetingScreenWrapper';
import { RxViewHorizontal } from 'react-icons/rx';
import { RxViewVertical } from 'react-icons/rx';
import { FaPlus } from 'react-icons/fa';
import { FaMinus } from 'react-icons/fa';
import { BiCollapseAlt } from 'react-icons/bi';
import { LuScreenShare } from 'react-icons/lu';

type Props = {
	participant?: DyteParticipant;
	screenShareParticipant?: DyteParticipant;
};

const PostJoinedScreen = ({ participant, screenShareParticipant }: Props) => {
	const dyteMeeting = useDyteMeeting();
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef<HTMLDyteParticipantTileElement>(null);

	const [isHorizontalLayout, setIsHorizontalLayout] = useState<boolean>(true);
	const [isParticipantTileAccordianOpen, setIsParticipantTileAccordianOpen] = useState<boolean>(true);
	const [isScreenTileAccordianOpen, setIsScreenTileAccordianOpen] = useState<boolean>(true);
	const [isDraggableChildVisible, setIsDraggableChildVisible] = useState<boolean>(true);

	const onClose = () => {
		setIsOpen(false);
	};
	const onOpen = () => {
		setIsOpen(false);
	};

	const toggleScreenshare = () => {
		if (isOpen) {
			onClose();
			exitFullSreen();
		} else {
			ref.current && requestFullScreen(ref.current);
			onOpen();
		}
	};

	return (
		<div className="h-full justify-between bg-white">
			<MeetingScreenWrapper />
			<DyteParticipantsAudio meeting={dyteMeeting.meeting} />
			{participant && (
				<Draggable defaultClassName={classNames('absolute bottom-44 left-6')}>
					{isDraggableChildVisible ? (
						<div className="border-[0.5px] border-solid border-[#3e3e3e] p-1 rounded-[4px] bg-[black]">
							{screenShareParticipant && (
								<div className="flex justify-between gap-2">
									{isHorizontalLayout ? (
										<RxViewVertical
											className="cursor-pointer"
											onClick={() => {
												setIsHorizontalLayout(!isHorizontalLayout);
											}}
										/>
									) : (
										<RxViewHorizontal
											className="cursor-pointer"
											onClick={() => {
												setIsHorizontalLayout(!isHorizontalLayout);
											}}
										/>
									)}

									<BiCollapseAlt onClick={() => setIsDraggableChildVisible(false)} className="cursor-pointer" />
								</div>
							)}
							<div className={`mt-1 gap-2 ${isHorizontalLayout ? 'flex flex-col' : 'flex flex-row'}`}>
								<div className="">
									<div className="border-[0.5px] border-solid border-[#3e3e3e] p-1">
										{isParticipantTileAccordianOpen ? (
											<FaMinus
												className="cursor-pointer"
												onClick={() => setIsParticipantTileAccordianOpen(!isParticipantTileAccordianOpen)}
											/>
										) : (
											<FaPlus
												className="cursor-pointer"
												onClick={() => setIsParticipantTileAccordianOpen(!isParticipantTileAccordianOpen)}
											/>
										)}
									</div>
									{isParticipantTileAccordianOpen ? (
										<div className="mt-1">
											<DyteParticipantTile
												ref={ref}
												participant={participant}
												meeting={dyteMeeting.meeting}
												style={{ height: '180px', width: '320px' }}
												className="relative"
											>
												<DyteControlbarButton
													className="absolute right-2 top-2 h-8 w-8"
													size="sm"
													onClick={toggleScreenshare}
													icon={isOpen ? defaultIconPack.full_screen_minimize : defaultIconPack.full_screen_maximize}
												/>
												<DyteAvatar size="lg" />
												<DyteNameTag participant={participant}>
													<DyteAudioVisualizer slot="start" participant={participant} />
												</DyteNameTag>
											</DyteParticipantTile>
										</div>
									) : null}
								</div>
								{screenShareParticipant ? (
									<div>
										<div className="border-[0.5px] border-solid border-[#3e3e3e] p-1">
											{isScreenTileAccordianOpen ? (
												<FaMinus
													className="cursor-pointer"
													onClick={() => setIsScreenTileAccordianOpen(!isScreenTileAccordianOpen)}
												/>
											) : (
												<FaPlus
													className="cursor-pointer"
													onClick={() => setIsScreenTileAccordianOpen(!isScreenTileAccordianOpen)}
												/>
											)}
										</div>
										{isScreenTileAccordianOpen ? (
											<div className="mt-1">
												<DyteScreenshareView
													meeting={dyteMeeting.meeting}
													participant={screenShareParticipant}
													style={{ height: '180px', width: '320px' }}
												>
													<DyteNameTag participant={screenShareParticipant}>
														<DyteAudioVisualizer slot="start" participant={screenShareParticipant} />
													</DyteNameTag>
												</DyteScreenshareView>
											</div>
										) : null}
									</div>
								) : null}
							</div>
						</div>
					) : (
						<div className="border-[0.5px] border-solid border-[#3e3e3e] p-2 rounded-[4px] bg-[black] flex gap-2 items-center cursor-default">
							<div className="text-sm">Participant/Screen</div>
							<LuScreenShare onClick={() => setIsDraggableChildVisible(true)} className="cursor-pointer" />
						</div>
					)}
				</Draggable>
			)}
		</div>
	);
};

export default PostJoinedScreen;
